import { Css, RouteTabWithContent, TabContent } from "@homebound/beam";
import { useMemo } from "react";
import { useParams } from "react-router";
import {
  createDocumentsUrl,
  createPlanPackageDetailsOptionDataUrl,
  createPlanPackageDetailsOverviewUrl,
} from "src/RouteUrls";
import {
  PlanPackagePageMetadataQuery,
  usePlanPackagePageMetadataQuery,
} from "src/routes/libraries/plan-package/details/PlanPackagePage.generated";
import { PlanPackageHeader } from "src/routes/libraries/plan-package/details/components/PlanPackageHeader";
import { OverviewTab } from "src/routes/libraries/plan-package/details/plan-overview/OverviewTab";
import { DocumentsPage } from "src/routes/projects/documents/DocumentsPage";
import {
  PlanPackageDetailsParams,
  planPackageDetailsDocuments,
  planPackageDetailsOptionData,
  planPackageDetailsOverview,
  planPackagePath,
} from "src/routes/routesDef";
import { queryResult } from "src/utils";
import { OptionDataTab } from "./OptionDataTab";

export function PlanPackagePage() {
  const { planPackageId, planPackageVersionId } = useParams<PlanPackageDetailsParams>();
  const planPageMetadataQuery = usePlanPackagePageMetadataQuery({
    variables: { id: planPackageId, versionId: planPackageVersionId },
  });

  return queryResult(planPageMetadataQuery, (data) => (
    <DataView planPackageId={planPackageId} metadata={{ ...data }} />
  ));
}

type DataViewProps = {
  planPackageId: string;
  metadata: PlanPackagePageMetadataQuery;
};

function DataView({ planPackageId, metadata }: DataViewProps) {
  const { planPackage, currentUser } = metadata;
  const tabs: RouteTabWithContent[] = useMemo(
    () => [
      {
        name: "Plan Overview",
        href: createPlanPackageDetailsOverviewUrl(planPackageId, planPackage.aggregateLatest.id),
        path: [planPackagePath, planPackageDetailsOverview],
        render: () => (
          <OverviewTab
            planPackageId={planPackageId}
            versionId={planPackage.aggregateLatest.id}
            currentUser={currentUser}
          />
        ),
      },
      {
        name: "Option Data",
        href: createPlanPackageDetailsOptionDataUrl(planPackageId, planPackage.aggregateLatest.id),
        path: planPackageDetailsOptionData,
        render: () => <OptionDataTab metadata={metadata} />,
      },
      {
        name: "Documents",
        href: createDocumentsUrl(planPackageId, { versionId: planPackage.aggregateLatest.id }),
        path: planPackageDetailsDocuments,
        render: () => <DocumentsPage readyPlanId={planPackageId} versionId={planPackage.aggregateLatest.id} />,
      },
    ],
    [currentUser, metadata, planPackage.aggregateLatest.id, planPackageId],
  );

  return (
    <>
      <PlanPackageHeader planPackage={planPackage} tabs={tabs} />
      <TabContent tabs={tabs} contentXss={Css.pt3.$} />
    </>
  );
}
